import gsap from "gsap";
import { Flip } from "gsap/Flip";

const DURATION = 0.7;
const EASE = "power2.out";
const WORK_DETAIL_NAMESPACES = ["work-detail", "work-detail-music"];

type MediaType = "video" | "image";

interface CapturedState {
    type: MediaType;
    src: string;
    currentTime: number;
    playbackRate: number;
    rect: { top: number; left: number; width: number; height: number };
    capturedAt: number;
}

let captured: CapturedState | null = null;
let overlayEl: HTMLElement | null = null;
let cardIndex: number = -1;

function getVideoSrc(video: HTMLVideoElement): string {
    if (video.currentSrc) return video.currentSrc;
    return video.querySelector<HTMLSourceElement>("source[src]")?.src ?? "";
}

function createVideoOverlay(state: CapturedState): HTMLVideoElement {
    const video = document.createElement("video");
    video.muted = true;
    video.loop = true;
    video.autoplay = true;
    video.playsInline = true;
    video.preload = "auto";
    video.src = state.src;
    video.playbackRate = state.playbackRate;

    const elapsed = Math.max(0, (Date.now() - state.capturedAt) / 1000);
    const startTime = state.currentTime + elapsed * state.playbackRate;

    video.addEventListener(
        "loadedmetadata",
        () => {
            video.currentTime = video.duration > 0 ? startTime % video.duration : startTime;
        },
        { once: true }
    );

    void video.play().catch(() => undefined);
    return video;
}

function createImageOverlay(state: CapturedState): HTMLImageElement {
    const img = document.createElement("img");
    img.src = state.src;
    img.alt = "";
    return img;
}

function createOverlay(state: CapturedState): HTMLElement {
    removeOverlayImmediate();

    const el = state.type === "video" ? createVideoOverlay(state) : createImageOverlay(state);

    el.style.cssText = `position:fixed;margin:0;z-index:3000;object-fit:cover;pointer-events:none;top:${state.rect.top}px;left:${state.rect.left}px;width:${state.rect.width}px;height:${state.rect.height}px`;

    document.body.appendChild(el);
    overlayEl = el;
    return el;
}

function animateTo(targetRect: { top: number; left: number; width: number; height: number }): Promise<void> {
    if (!overlayEl) return Promise.resolve();

    const el = overlayEl;
    const state = Flip.getState(el);

    gsap.set(el, {
        top: targetRect.top,
        left: targetRect.left,
        width: targetRect.width,
        height: targetRect.height,
    });

    return new Promise(resolve => {
        Flip.from(state, {
            duration: DURATION,
            ease: EASE,
            absolute: true,
            onComplete: resolve,
        });
    });
}

function removeOverlayImmediate() {
    if (overlayEl) {
        overlayEl.remove();
        overlayEl = null;
    }
}

export function cleanup() {
    removeOverlayImmediate();
    captured = null;
}

export function dismissOverlay(): Promise<void> {
    if (!overlayEl) return Promise.resolve();

    const el = overlayEl;
    overlayEl = null;

    return new Promise(resolve => {
        gsap.to(el, {
            opacity: 0,
            duration: 0.14,
            ease: "none",
            onComplete() {
                el.remove();
                resolve();
            },
        });
    });
}

export function isWorkDetailNamespace(ns: string): boolean {
    return WORK_DETAIL_NAMESPACES.includes(ns);
}

export function captureFromWorkCard(trigger: HTMLElement): boolean {
    const link = trigger.closest<HTMLAnchorElement>("a");
    if (!link) return false;

    const card = link.closest<HTMLElement>(".work-card");
    if (!card) return false;

    const video = card.querySelector<HTMLVideoElement>(".work-card__video--own video");
    if (!video) return false;

    const src = getVideoSrc(video);
    if (!src) return false;

    const rect = video.getBoundingClientRect();
    captured = {
        type: "video",
        src,
        currentTime: video.currentTime || 0,
        playbackRate: video.playbackRate || 1,
        rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
        capturedAt: Date.now(),
    };

    const listItem = card.closest<HTMLElement>("li");
    cardIndex = listItem ? Array.from(listItem.parentElement?.children ?? []).indexOf(listItem) : -1;
    return true;
}

export function captureFromHeaderVideo(link: HTMLElement): boolean {
    const header = link.closest<HTMLElement>('header[data-header="full"]');
    if (!header) return false;

    const videoWrap = header.querySelector<HTMLElement>(".header__work-video");
    const video = videoWrap?.querySelector<HTMLVideoElement>("video");
    if (!video || !video.src) return false;

    const src = getVideoSrc(video);
    if (!src) return false;

    const rect = videoWrap!.getBoundingClientRect();
    captured = {
        type: "video",
        src,
        currentTime: video.currentTime || 0,
        playbackRate: video.playbackRate || 1,
        rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
        capturedAt: Date.now(),
    };

    cardIndex = -1;
    return true;
}

export function captureFromWorkDetail(container: HTMLElement): boolean {
    const target = container.querySelector<HTMLElement>("[data-work-detail-video-target]");
    if (!target) return false;

    const wrap = target.closest<HTMLElement>(".video_wrap");
    const video = wrap?.querySelector<HTMLVideoElement>("video");
    if (!video) return false;

    const rect = target.getBoundingClientRect();
    captured = {
        type: "video",
        src: getVideoSrc(video),
        currentTime: video.currentTime || 0,
        playbackRate: video.playbackRate || 1,
        rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
        capturedAt: Date.now(),
    };

    if (wrap) wrap.style.visibility = "hidden";
    return true;
}

export function captureFromNewsCard(trigger: HTMLElement): boolean {
    const link = trigger.closest<HTMLAnchorElement>("a.news-card");
    if (!link) return false;

    const img = link.querySelector<HTMLImageElement>(".news-card__asset img");
    if (!img) return false;

    const rect = img.getBoundingClientRect();
    captured = {
        type: "image",
        src: img.currentSrc || img.src,
        currentTime: 0,
        playbackRate: 1,
        rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
        capturedAt: Date.now(),
    };

    const listItem = link.closest<HTMLElement>("li");
    cardIndex = listItem ? Array.from(listItem.parentElement?.children ?? []).indexOf(listItem) : -1;
    return true;
}

export function captureFromNewsDetail(container: HTMLElement): boolean {
    const hero = container.querySelector<HTMLElement>(".detail-hero__bg");
    if (!hero) return false;

    const img = hero.querySelector<HTMLImageElement>("img");
    if (!img) return false;

    const rect = hero.getBoundingClientRect();
    captured = {
        type: "image",
        src: img.currentSrc || img.src,
        currentTime: 0,
        playbackRate: 1,
        rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
        capturedAt: Date.now(),
    };

    hero.style.visibility = "hidden";
    return true;
}

export function hasPending(): boolean {
    return captured !== null;
}

export function getPendingType(): MediaType | null {
    return captured?.type ?? null;
}

export function initTransitionCapture() {
    document.addEventListener(
        "click",
        event => {
            const target = event.target;
            if (!(target instanceof Element)) return;

            const headerWorkLink = target.closest<HTMLAnchorElement>('header[data-header="full"] .header__nav-group--work .header__nav-list a[data-header-video]');
            if (headerWorkLink) {
                captureFromHeaderVideo(headerWorkLink);
                fadeHeaderOut();
                return;
            }

            const workLink = target.closest<HTMLAnchorElement>("a.work-card__bg");
            if (workLink) {
                captureFromWorkCard(workLink);
                fadeHeaderOut();
                return;
            }

            const newsLink = target.closest<HTMLAnchorElement>("a.news-card");
            if (newsLink) {
                captureFromNewsCard(newsLink);
                fadeHeaderOut();
            }
        },
        { capture: true }
    );
}

function fadeHeaderOut() {
    const header = document.querySelector<HTMLElement>(".header:not([hidden])");
    if (header) gsap.to(header, { opacity: 0, duration: 0.15, ease: "power2.out" });
}

export async function animateWorkForward(nextContainer: HTMLElement): Promise<void> {
    if (!captured) return;

    const target = nextContainer.querySelector<HTMLElement>("[data-work-detail-video-target]");
    if (!target) {
        cleanup();
        return;
    }

    const wrap = target.closest<HTMLElement>(".video_wrap");
    if (wrap) wrap.classList.add("is-transitioning-in");

    createOverlay(captured);

    const targetRect = target.getBoundingClientRect();
    if (targetRect.width <= 0 || targetRect.height <= 0) {
        cleanup();
        if (wrap) wrap.classList.remove("is-transitioning-in");
        return;
    }

    await animateTo({ top: targetRect.top, left: targetRect.left, width: targetRect.width, height: targetRect.height });

    const detailVideo = wrap?.querySelector<HTMLVideoElement>(".vim_video");
    if (detailVideo && overlayEl instanceof HTMLVideoElement) {
        detailVideo.muted = true;
        detailVideo.currentTime = overlayEl.currentTime;
        detailVideo.playbackRate = overlayEl.playbackRate;
        void detailVideo.play().catch(() => undefined);
    }

    if (wrap) wrap.classList.remove("is-transitioning-in");
    window.dispatchEvent(new CustomEvent("work-detail-video-transition-finished"));
    captured = null;
}

export async function animateNewsForward(nextContainer: HTMLElement): Promise<void> {
    if (!captured) return;

    const hero = nextContainer.querySelector<HTMLElement>(".detail-hero__bg");
    if (!hero) {
        cleanup();
        return;
    }

    hero.style.opacity = "0";

    createOverlay(captured);

    const targetRect = hero.getBoundingClientRect();
    if (targetRect.width <= 0 || targetRect.height <= 0) {
        cleanup();
        hero.style.opacity = "";
        return;
    }

    await animateTo({ top: targetRect.top, left: targetRect.left, width: targetRect.width, height: targetRect.height });

    hero.style.opacity = "";
    captured = null;
}

export async function animateWorkReverse(nextContainer: HTMLElement): Promise<void> {
    if (!captured) return;

    createOverlay(captured);

    const targetRect = findWorkCardRect(nextContainer);
    if (!targetRect) {
        await dismissOverlay();
        captured = null;
        return;
    }

    await animateTo(targetRect);
    await dismissOverlay();
    captured = null;
}

export async function animateNewsReverse(nextContainer: HTMLElement): Promise<void> {
    if (!captured) return;

    createOverlay(captured);

    const targetRect = findNewsCardRect(nextContainer);
    if (!targetRect) {
        await dismissOverlay();
        captured = null;
        return;
    }

    await animateTo(targetRect);
    await dismissOverlay();
    captured = null;
}

function getRectFromEl(el: HTMLElement): { top: number; left: number; width: number; height: number } | null {
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
        return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
    }
    return null;
}

function findWorkCardRect(container: HTMLElement): { top: number; left: number; width: number; height: number } | null {
    if (cardIndex < 0) return null;

    const items = container.querySelectorAll<HTMLElement>(".work-card");
    const card = items[cardIndex];
    if (!card) return null;

    const video = card.querySelector<HTMLVideoElement>(".work-card__video--own video");
    return getRectFromEl(video || card);
}

function findNewsCardRect(container: HTMLElement): { top: number; left: number; width: number; height: number } | null {
    if (cardIndex < 0) return null;

    const items = container.querySelectorAll<HTMLElement>("a.news-card");
    const link = items[cardIndex];
    if (!link) return null;

    const img = link.querySelector<HTMLImageElement>(".news-card__asset img");
    return getRectFromEl(img || link);
}
